export class CacheStore {
    private dbName: string;
    private storeName: string;
    private db: IDBDatabase | null;

    private constructor(dbName: string, storeName: string) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.db = null;
    }

    public static create(storeName: string) {
        return new CacheStore('cache', storeName);
    }

    private async openDb(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, 1);

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, {keyPath: '__id'});
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onerror = () => {
                reject(`Failed to open database: ${request.error}`);
            };
        });
    }

    private getDb(): Promise<IDBDatabase> {
        if (this.db) {
            return Promise.resolve(this.db);
        }
        return this.openDb();
    }

    public async store(id: string, data: any): Promise<void> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.put({__id: id, ...data});

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(`Failed to store data: ${request.error}`);
            };
        });
    }

    public async retrieve(id: string): Promise<any> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.get(id);

            request.onsuccess = () => {
                if (request.result) {
                    resolve(request.result);
                } else {
                    resolve(null);
                }
            };

            request.onerror = () => {
                reject(`Failed to retrieve data: ${request.error}`);
            };
        });
    }

    public async delete(id: string): Promise<void> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.delete(id);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(`Failed to delete data: ${request.error}`);
            };
        });
    }
}
